import { createElement, useState } from 'react';

import type { BlockRecord } from '@/lib/blocks/types';
import type { BlockPropertyRecord, DatabaseColumnDefinition } from '@/lib/databases/types';
import { boardLaneDropKey, buildBoardLanes, type BoardLane } from '@/lib/databases/board-lanes';
import type { DatabaseQueryGroup } from '@/lib/databases/types';
import { formatCellDisplay, type SelectColor } from '@couplespace/core/databases';
import { SelectChip } from './SelectChip.web';

type DatabaseBoardProps = {
  groups: DatabaseQueryGroup[];
  boardColumn: DatabaseColumnDefinition | undefined;
  columns: DatabaseColumnDefinition[];
  propertiesByRowId: Record<string, BlockPropertyRecord[]>;
  boardColumnId: string | undefined;
  disabled?: boolean;
  onMoveCard: (rowId: string, laneKey: string | null) => Promise<void>;
  onOpenRow?: (rowId: string) => void;
  onPeekRow?: (rowId: string) => void;
};

function stopBubble(event: { stopPropagation: () => void }) {
  event.stopPropagation();
}

/** In-column scroll only when a lane exceeds this many cards. */
const BOARD_LANE_SCROLL_THRESHOLD = 20;

function cardPreview(
  row: BlockRecord,
  properties: BlockPropertyRecord[],
  previewColumnIds: string[],
  columnsById: Map<string, DatabaseColumnDefinition>,
): string[] {
  const lines: string[] = [];
  for (const colId of previewColumnIds) {
    const col = columnsById.get(colId);
    if (!col) {
      continue;
    }
    const prop = properties.find((p) => p.propertyId === colId);
    const text = formatCellDisplay(col.type, prop?.value ?? null, {
      columnConfig: col.config,
    });
    if (text) {
      lines.push(text);
    }
  }
  return lines.slice(0, 2);
}

export function DatabaseBoard({
  groups,
  boardColumn,
  columns,
  propertiesByRowId,
  boardColumnId,
  disabled,
  onMoveCard,
  onOpenRow,
  onPeekRow,
}: DatabaseBoardProps) {
  const [draggingRowId, setDraggingRowId] = useState<string | null>(null);
  const lanes: BoardLane[] = buildBoardLanes(groups, boardColumn);
  const columnsById = new Map(columns.map((c) => [c.id, c]));
  const previewColumnIds = columns
    .filter((c) => c.id !== boardColumnId)
    .slice(0, 2)
    .map((c) => c.id);

  async function handleDrop(lane: BoardLane) {
    if (!draggingRowId || disabled) {
      return;
    }
    const rowId = draggingRowId;
    setDraggingRowId(null);
    await onMoveCard(rowId, boardLaneDropKey(lane));
  }

  return createElement(
    'div',
    {
      className: 'cs-db-board',
      onMouseDown: stopBubble,
      onKeyDownCapture: stopBubble,
    },
    ...lanes.map((lane) =>
      createElement(
        'div',
        {
          key: boardLaneDropKey(lane),
          className:
            lane.rows.length >= BOARD_LANE_SCROLL_THRESHOLD
              ? 'cs-db-board-lane cs-db-board-lane-scroll'
              : 'cs-db-board-lane',
          onDragOver: (e: DragEvent) => {
            e.preventDefault();
            if (e.dataTransfer) {
              e.dataTransfer.dropEffect = 'move';
            }
          },
          onDrop: (e: DragEvent) => {
            e.preventDefault();
            stopBubble(e);
            void handleDrop(lane);
          },
        },
        createElement(
          'div',
          { className: 'cs-db-board-lane-header' },
          lane.color
            ? createElement(SelectChip, {
                label: lane.label,
                color: lane.color as SelectColor,
              })
            : createElement('span', { className: 'cs-db-board-lane-title' }, lane.label),
          createElement('span', { className: 'cs-db-board-lane-count' }, String(lane.rows.length)),
        ),
        createElement(
          'div',
          { className: 'cs-db-board-lane-cards' },
          ...lane.rows.map((row) => {
            const props = propertiesByRowId[row.id] ?? [];
            const previews = cardPreview(row, props, previewColumnIds, columnsById);
            return createElement(
              'div',
              {
                key: row.id,
                className: 'cs-db-board-card',
                draggable: !disabled,
                onDragStart: (e: DragEvent) => {
                  setDraggingRowId(row.id);
                  if (e.dataTransfer) {
                    e.dataTransfer.effectAllowed = 'move';
                    e.dataTransfer.setData('text/plain', row.id);
                  }
                  stopBubble(e);
                },
                onDragEnd: () => setDraggingRowId(null),
                onDoubleClick: () => onOpenRow?.(row.id),
                onMouseDown: stopBubble,
              },
              createElement(
                'div',
                { className: 'cs-db-board-card-title' },
                row.title?.trim() || 'Untitled',
              ),
              ...previews.map((line, i) =>
                createElement('div', { key: i, className: 'cs-db-board-card-preview' }, line),
              ),
              onPeekRow
                ? createElement(
                    'div',
                    { className: 'cs-db-board-card-actions' },
                    createElement('button', {
                      type: 'button',
                      className: 'cs-db-peek-btn',
                      onClick: (e: MouseEvent) => {
                        e.stopPropagation();
                        onPeekRow(row.id);
                      },
                      onMouseDown: stopBubble,
                    }, 'Open'),
                  )
                : null,
            );
          }),
          lane.rows.length === 0
            ? createElement('div', { className: 'cs-db-board-lane-empty' }, 'Drop here')
            : null,
        ),
      ),
    ),
  );
}
